import type { ComputedRef, Ref } from 'vue'
import type { DateRequestFormModel } from '~/composables/useDateRequestFormState'

/** At this size and above, use private-event inquiry flow (not standard date request). */
export const PRIVATE_EVENT_MIN_TICKETS = 11
export const DATE_REQUEST_PURCHASE_MIN_TICKETS = 5
export const DATE_REQUEST_PURCHASE_MAX_TICKETS = 10

export interface DateRequestFlow {
  isFromEventPage: ComputedRef<boolean>
  isDateRequestEnabledForTheEvent: ComputedRef<boolean>
  isInstantBookingOfferOnEventPage: ComputedRef<boolean>
  isPrivateEventRequest: ComputedRef<boolean>
  isDateRequestApplicable: ComputedRef<boolean>
  canPurchaseTicketsViaDateRequest: ComputedRef<boolean>
  isInstantDateRequestSimplifiedUi: ComputedRef<boolean>
  userHasCommittedTicketCount: Ref<boolean>
}

/**
 * Derives the set of flow flags that determine which variant of the
 * date-request UI to render and which submission branch to use.
 */
export const useDateRequestFlow = (
  form: Ref<DateRequestFormModel>
): DateRequestFlow => {
  const eventData = useEventData()
  const { eventRequestOpenedPage, isDateRequestSidebarOpen } = useUiState()

  const userHasCommittedTicketCount = ref(false)

  // Reset the commit flag whenever the sidebar is opened; the user needs to
  // re-confirm a count for a fresh session.
  watch(isDateRequestSidebarOpen, (open) => {
    if (open) {
      userHasCommittedTicketCount.value = false
    }
  })

  const isFromEventPage = computed(
    () => eventRequestOpenedPage?.value?.split('___')[0] === 'e-eventPermalink'
  )

  const isDateRequestEnabledForTheEvent = computed(() =>
    eventGetters.isDateRequestActive(eventData.value)
  )

  const isInstantBookingOfferOnEventPage = computed(
    () =>
      isFromEventPage.value &&
      isDateRequestEnabledForTheEvent.value &&
      eventGetters.isInstantBookingActive(eventData.value)
  )

  /** Until the user sets party size, use date-request + instant-style UI instead of private flow. */
  const isPrivateEventRequest = computed(() => {
    const n = form.value?.numberOfTickets
    if (
      isInstantBookingOfferOnEventPage.value &&
      (n === null || n === undefined || n === 0)
    ) {
      return false
    }
    if (!n || n === 0) {
      return true
    }
    return n >= PRIVATE_EVENT_MIN_TICKETS
  })

  const isDateRequestApplicable = computed(
    () =>
      isFromEventPage.value &&
      !isPrivateEventRequest.value &&
      isDateRequestEnabledForTheEvent.value
  )

  const canPurchaseTicketsViaDateRequest = computed(() => {
    const n = form.value.numberOfTickets
    if (n === null || n === undefined) {
      return false
    }
    return (
      isDateRequestApplicable.value &&
      n >= DATE_REQUEST_PURCHASE_MIN_TICKETS &&
      n <= DATE_REQUEST_PURCHASE_MAX_TICKETS
    )
  })

  /**
   * Use instant booking subform (participants + one date): before party size is committed, always;
   * afterwards only when count is inside the instant band.
   */
  const isInstantDateRequestSimplifiedUi = computed(() => {
    if (!isDateRequestApplicable.value) {
      return false
    }
    const ev = eventData.value
    if (!eventGetters.isInstantBookingActive(ev)) {
      return false
    }
    const n = form.value.numberOfTickets
    const unset = n === null || n === undefined || n === 0

    if (!userHasCommittedTicketCount.value && unset) {
      return true
    }
    if (unset) {
      return false
    }
    const min = eventGetters.getMinInstantBookingTickets(ev)
    const max = eventGetters.getMaxInstantBookingTickets(ev)
    return n >= min && n <= max
  })

  return {
    isFromEventPage,
    isDateRequestEnabledForTheEvent,
    isInstantBookingOfferOnEventPage,
    isPrivateEventRequest,
    isDateRequestApplicable,
    canPurchaseTicketsViaDateRequest,
    isInstantDateRequestSimplifiedUi,
    userHasCommittedTicketCount,
  }
}
